/**
 * Step 6 — AI Act Classification.
 *
 * Was step 7. Moved ahead of the (optional) Linked Artefacts step by product decision — see
 * `AiUseCaseWizard.tsx`'s file header for the full account, including the one real side
 * effect: this step and step 8 (Oversight & Vendors) are no longer adjacent, so the two AI
 * Act steps now have the unrelated Linked Artefacts step sitting between them.
 *
 * This is the step the whole extension exists for. The risk tier is the organising concept
 * the ported register was missing: it is the only field here that *gates* — `prohibited`
 * stops the flow outright and `high_risk` pulls in the step 8 oversight requirements that
 * no other tier needs. It is deliberately not derived from `aiType`, which classifies supply
 * topology and drives nothing.
 *
 * Four conditional clusters, each mirroring a server rule in
 * `prod-nimbus-ai-use-cases/lambda_function.py` exactly — the client must not gate on
 * anything the server does not, or the wizard blocks a record the API would accept, and it
 * must not skip a gate the server applies, or Submit fails with a 400 the user cannot see:
 *
 *   1. `prohibited` blocks forward navigation and submission (`prohibited_classification_error`,
 *      `:836-847`). The shell renders the same block above the Back/Next bar, so it follows
 *      the user rather than only living on this step.
 *   2. `classificationRationale` is required for `high_risk` only (`validate_ai_act`, `:878`).
 *      Asked of every tier, because a tier with no reasoning is not defensible — but only
 *      *enforced* where the server enforces it.
 *   3. `automatedDecisionDetails` is required — and offered — only while
 *      `makesAutomatedDecisions` is set. NOT while `involvesProfiling` alone is set: the
 *      server clears the field unless `makesAutomatedDecisions` (`:835-836`), so requiring it
 *      for profiling-only would be an unsatisfiable gate — the user types the text, the server
 *      drops it, and the step never passes. Expressed via `needsAutomatedDecisionDetails()`,
 *      which now carries this narrow rule for all four surfaces; it previously returned true
 *      for either flag, and this step's inlined workaround is what let the detail page and the
 *      PDF keep the broad version and render a Required field nobody could satisfy.
 *   4. `isGpaiSystemic` implies `isGpai` (Art 51 — systemic risk is a property of a GPAI
 *      model), which the server also enforces on write. GPAI is a SEPARATE AXIS from the
 *      tier, never a fifth tier: a system can be Annex III high-risk *and* GPAI.
 */
import React from 'react';
import { RichTextField } from '../../../components/RichTextField';
import { AiRiskAssessmentPanel } from './AiRiskAssessmentPanel';
import {
  ACT_ROLES,
  ACT_ROLE_DESCRIPTIONS,
  ACT_ROLE_ICONS,
  ACT_ROLE_LABELS,
  ACT_ROLE_TONE,
  ADM_FLAGS,
  AI_ACT_CLASSIFICATIONS,
  AI_ACT_CLASSIFICATION_DESCRIPTIONS,
  AI_ACT_CLASSIFICATION_ICONS,
  AI_ACT_CLASSIFICATION_LABELS,
  AI_ACT_CLASSIFICATION_TONE,
  GPAI_TIER_DESCRIPTIONS,
  GPAI_TIER_ICONS,
  GPAI_TIER_LABELS,
  GPAI_TIER_TONE,
  gpaiTier,
  isBlocking,
  needsAutomatedDecisionDetails,
} from '../../../constants/aiAct';
import type { ActRole, AiActClassification, AiUseCase } from '../../../types/models';
import type { AdmFlagSpec } from '../../../constants/aiAct';
import type { StepProps } from './types';

/** Required marker. Decorative — the accessible signal is `required` on the control itself. */
const Req: React.FC = () => (
  <span className="text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

const Step6Classification: React.FC<StepProps> = ({ useCase, update }) => {
  const classification = useCase.aiActClassification || '';
  const prohibited = isBlocking(classification);
  const isHighRisk = classification === 'high_risk';
  const tier = gpaiTier({ isGpai: !!useCase.isGpai, isGpaiSystemic: !!useCase.isGpaiSystemic });
  const admDetailsRequired = needsAutomatedDecisionDetails({
    makesAutomatedDecisions: !!useCase.makesAutomatedDecisions,
  });

  /** Cluster 3 — turning the decisions flag off clears its detail field, because the server
   *  does the same on write. Doing it here too means the cleared value is visible immediately
   *  rather than reappearing as a surprise after the next reload. */
  const setAdmFlag = (flag: AdmFlagSpec, value: boolean) => {
    if (flag.field === 'makesAutomatedDecisions' && !value) {
      update({ makesAutomatedDecisions: false, automatedDecisionDetails: '' });
      return;
    }
    update({ [flag.field]: value } as Partial<AiUseCase>);
  };

  return (
    <div>
      <p className="np-sub mb-4">
        Everything so far described what this AI <em>does</em>. This step decides how heavily it
        is regulated — and that answer changes what the rest of the record has to contain. Effort
        scales with risk: a minimal-risk tool asks almost nothing more of you.
      </p>

      {/* ── Cluster 1 — the tier ───────────────────────────────────────────── */}
      <fieldset className="np-fieldset">
        <legend className="np-fieldset-legend">
          EU AI Act risk classification
          <Req />
        </legend>
        <p className="np-hint mt-0 mb-3">
          Pick the highest tier that applies. Unclassified is not the same as minimal risk —
          it means nobody has answered the question yet.
        </p>

        <div className="np-choice-list">
          {AI_ACT_CLASSIFICATIONS.map(value => {
            const selected = classification === value;
            const tone = AI_ACT_CLASSIFICATION_TONE[value];
            const copy = AI_ACT_CLASSIFICATION_DESCRIPTIONS[value];
            return (
              <label key={value} className={`np-choice${selected ? ` selected np-tone-${tone}` : ''}`}>
                <input
                  type="radio"
                  name="ai-act-classification"
                  className="flex-shrink-0 mt-1"
                  value={value}
                  checked={selected}
                  required
                  onChange={() => update({ aiActClassification: value as AiActClassification })}
                />
                <span className={`np-icon-badge np-tone-${tone}`}>
                  <i className={AI_ACT_CLASSIFICATION_ICONS[value]}></i>
                </span>
                <div className="np-choice-body">
                  <div className="np-choice-label d-flex align-items-center gap-2 flex-wrap">
                    {AI_ACT_CLASSIFICATION_LABELS[value]}
                    <span className={`np-chip np-tone-${tone}`}>{copy.article}</span>
                  </div>
                  <p className="np-choice-desc">{copy.short}</p>
                </div>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* The blocking state. Rendered here in full, and again in the shell above the Back/Next
       *  bar so it is not left behind when the user pages away from this step. */}
      {prohibited && (
        <div className="np-banner np-tone-danger mb-3" role="alert">
          <i className="ri-forbid-2-line"></i>
          <div>
            <strong>This is a prohibited AI practice (Article 5). Intake stops here.</strong>
            <p className="mb-0 mt-1">
              A prohibited practice cannot be submitted, approved, mitigated or risk-accepted —
              there is no safeguard that makes it lawful. Either this classification is wrong and
              should be corrected above, or the use case has to be abandoned. Nothing further in
              this wizard will unlock while it stands.
            </p>
          </div>
        </div>
      )}

      {classification ? (
        <div className={`np-card np-card-accent-${AI_ACT_CLASSIFICATION_TONE[classification]} mb-4`}>
          <div className="np-card-body">
            <div className="d-flex align-items-start gap-3">
              <span className={`np-icon-badge np-tone-${AI_ACT_CLASSIFICATION_TONE[classification]}`}>
                <i className={AI_ACT_CLASSIFICATION_ICONS[classification]}></i>
              </span>
              <div className="flex-grow-1">
                <div className="fw-semibold mb-1">
                  {AI_ACT_CLASSIFICATION_LABELS[classification]}
                </div>
                <p className="np-sub mb-3" style={{ lineHeight: 1.6 }}>
                  {AI_ACT_CLASSIFICATION_DESCRIPTIONS[classification].detail}
                </p>
                {AI_ACT_CLASSIFICATION_DESCRIPTIONS[classification].examples.length > 0 && (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="np-hint m-0">Typical examples</span>
                    {AI_ACT_CLASSIFICATION_DESCRIPTIONS[classification].examples.map(example => (
                      <span
                        key={example}
                        className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[classification]}`}
                      >
                        {example}
                      </span>
                    ))}
                  </div>
                )}
                <p className="np-hint mb-0 mt-2">
                  The examples are illustrative, not a checklist — a system can belong to a tier
                  without matching any of them. That is what the rationale below is for.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="np-banner np-tone-neutral mb-4">
          <i className="ri-arrow-up-line"></i>
          <span>Select a tier above to see what it means and what it will ask of you</span>
        </div>
      )}

      {/* ── Cluster 2 — rationale, required for high risk ───────────────────── */}
      <div className="np-field">
        <label htmlFor="ai-act-rationale">
          Why this classification?
          {isHighRisk && <Req />}
        </label>
        <RichTextField
          value={useCase.classificationRationale || ''}
          onChange={html => update({ classificationRationale: html })}
          placeholder="What the system decides or influences, about whom, and which part of the Act you read to land on this tier. Note anything you considered and ruled out."
          height={140}
        />
        <p className="np-hint">
          {isHighRisk
            ? 'Required for a high-risk system: the tier on its own is an assertion, and a regulator asks how it was reached. Kept in the audit trail, so a later reclassification does not erase the original reasoning.'
            : 'Not required at this tier, but the record is far more useful with it — especially the reasoning for why a heavier tier does not apply.'}
        </p>
      </div>

      <hr className="np-divider" />

      {/* ── AI Risk Assessment (EU AI Act Article 9) ────────────────────────── */}
      {!prohibited && <AiRiskAssessmentPanel useCase={useCase} update={update} />}

      <hr className="np-divider" />

      {/* ── Cluster 4 — GPAI, a separate axis from the tier ─────────────────── */}
      <div className="mb-3">
        <div className="np-fieldset-legend">General-purpose AI</div>
        <p className="np-sub mb-0">
          A separate question from the tier above, not a fifth tier — a high-risk system can be
          built on a general-purpose model, and so can a minimal-risk one. This asks about the
          model underneath, not about how much you rely on it.
        </p>
      </div>

      <div className="np-card mb-3">
        <div className="np-card-body">
          <div className="d-flex align-items-center gap-2 mb-2">
            <label className="np-switch mb-0" htmlFor="ai-act-gpai">
              <input
                type="checkbox"
                id="ai-act-gpai"
                checked={!!useCase.isGpai}
                // Clearing the base flag clears systemic with it: systemic risk is a property
                // of a general-purpose model, so it cannot stand on its own. The server applies
                // the same implication on write.
                onChange={e =>
                  update(
                    e.target.checked
                      ? { isGpai: true }
                      : { isGpai: false, isGpaiSystemic: false },
                  )
                }
              />
              <span className="np-switch-track"></span>
              <span className="np-switch-thumb"></span>
            </label>
            <label htmlFor="ai-act-gpai" className="mb-0">
              This runs on a general-purpose model
            </label>
          </div>
          <p className="np-hint mt-0 mb-0">
            {GPAI_TIER_DESCRIPTIONS.gpai.detail}
          </p>

          {useCase.isGpai && (
            <>
              <hr className="np-divider" />
              <div className="d-flex align-items-center gap-2 mb-2">
                <label className="np-switch mb-0" htmlFor="ai-act-gpai-systemic">
                  <input
                    type="checkbox"
                    id="ai-act-gpai-systemic"
                    checked={!!useCase.isGpaiSystemic}
                    onChange={e => update({ isGpai: true, isGpaiSystemic: e.target.checked })}
                  />
                  <span className="np-switch-track"></span>
                  <span className="np-switch-thumb"></span>
                </label>
                <label htmlFor="ai-act-gpai-systemic" className="mb-0">
                  The model is designated as carrying systemic risk
                </label>
              </div>
              <p className="np-hint mt-0 mb-0">{GPAI_TIER_DESCRIPTIONS.gpai_systemic.detail}</p>
            </>
          )}

          <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
            <span className="np-hint m-0">Recorded as</span>
            <span className={`np-chip np-tone-${GPAI_TIER_TONE[tier]}`}>
              <i className={`${GPAI_TIER_ICONS[tier]} me-1`}></i>
              {GPAI_TIER_LABELS[tier]}
            </span>
            <span className="np-hint m-0">{GPAI_TIER_DESCRIPTIONS[tier].article}</span>
          </div>
        </div>
      </div>

      <hr className="np-divider" />

      {/* ── Role in the value chain ─────────────────────────────────────────── */}
      <fieldset className="np-fieldset">
        <legend className="np-fieldset-legend">
          Our role
          <Req />
        </legend>
        <p className="np-hint mt-0 mb-3">
          Which obligations attach depends entirely on this. It is not a rewording of the AI type
          from step 1 — that describes where the AI came from, this describes what we are
          answerable for.
        </p>

        <div className="np-choice-list">
          {ACT_ROLES.map(role => {
            const selected = useCase.actRole === role;
            const tone = ACT_ROLE_TONE[role];
            return (
              <label key={role} className={`np-choice${selected ? ` selected np-tone-${tone}` : ''}`}>
                <input
                  type="radio"
                  name="ai-act-role"
                  className="flex-shrink-0 mt-1"
                  value={role}
                  checked={selected}
                  required
                  onChange={() => update({ actRole: role as ActRole })}
                />
                <span className={`np-icon-badge np-tone-${tone}`}>
                  <i className={ACT_ROLE_ICONS[role]}></i>
                </span>
                <div className="np-choice-body">
                  <div className="np-choice-label">{ACT_ROLE_LABELS[role]}</div>
                  <p className="np-choice-desc">{ACT_ROLE_DESCRIPTIONS[role].short}</p>
                  {selected && (
                    <p className="np-choice-desc mb-0">{ACT_ROLE_DESCRIPTIONS[role].detail}</p>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </fieldset>

      <hr className="np-divider" />

      {/* ── Cluster 3 — automated decisions and profiling ───────────────────── */}
      <div className="mb-3">
        <div className="np-fieldset-legend">Decisions about people</div>
        <p className="np-sub mb-0">
          The two questions this register never asked. Between them they are the trigger for GDPR
          Article 22, for an Annex III look at the high-risk tier, and for most internal AI
          policies.
        </p>
      </div>

      {ADM_FLAGS.map(flag => {
        const value = !!useCase[flag.field];
        return (
          <div className={`np-card mb-3${value ? ` np-card-accent-${flag.tone}` : ''}`} key={flag.field}>
            <div className="np-card-body">
              <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                <div className="d-flex align-items-start gap-3" style={{ flex: '1 1 20rem' }}>
                  <span className={`np-icon-badge np-tone-${value ? flag.tone : 'neutral'}`}>
                    <i className={flag.icon}></i>
                  </span>
                  <div>
                    <span className="np-field-caption">{flag.label}</span>
                    <p className="np-hint m-0">{flag.description}</p>
                  </div>
                </div>
                <div className="np-pill-toggle-group">
                  <button
                    type="button"
                    className={`np-pill-toggle${value ? ` active np-tone-${flag.tone}` : ''}`}
                    aria-pressed={value}
                    onClick={() => setAdmFlag(flag, true)}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`np-pill-toggle${!value ? ' active np-tone-neutral' : ''}`}
                    aria-pressed={!value}
                    onClick={() => setAdmFlag(flag, false)}
                  >
                    No
                  </button>
                </div>
              </div>

              {value && (
                <div className={`np-banner np-tone-${flag.tone} mt-3`}>
                  <i className="ri-information-line"></i>
                  <div>{flag.trigger}</div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {admDetailsRequired && (
        <div className="np-field">
          <label htmlFor="ai-act-adm-details">
            What is decided, about whom, and what can the person affected do about it?
            <Req />
          </label>
          <textarea
            id="ai-act-adm-details"
            className="np-textarea"
            rows={4}
            placeholder="e.g. The model scores inbound applications and auto-declines anything under 40. Declined applicants are told a model was used, and can ask for a manual review by the credit team within 14 days."
            value={useCase.automatedDecisionDetails || ''}
            required
            onChange={e => update({ automatedDecisionDetails: e.target.value })}
          />
          <p className="np-hint">
            Plain text, not rich text. Required because this system makes or materially informs
            decisions about people — and it is only stored while that answer stays yes.
          </p>
        </div>
      )}

      {!admDetailsRequired && useCase.involvesProfiling && (
        <div className="np-banner np-tone-info">
          <i className="ri-information-line"></i>
          <div>
            You have recorded profiling without automated decision-making. That is a coherent
            answer — describe the profiling itself in the business case and the data step. The
            decision-detail field opens only for the decisions question above, because that is the
            only answer the register keeps it against.
          </div>
        </div>
      )}
    </div>
  );
};

export default Step6Classification;
